import React from 'react';
import styled from 'styled-components';

import MediaPreview from '../../../components/media-preview/MediaPreview';
import CommentField from '../../../components/comment-field/CommentField';
import { MediaItem } from '../../../app/services/mediaItems';
import { MediaPreviewHandle, SizeMode } from '../../../types/media-preview';
import { getMediaUrl, toMediaType } from '../previewMediaUtils';
import ClientTagChips from './ClientTagChips';
import ProducerTagOverlay from './ProducerTagOverlay';
import type {
  MediaItemProducerTag,
  MediaItemClientTag,
  MediaTagSide,
} from '../../../app/services/mediaItems';

/**
 * 比較グループスライド。
 *
 * ファイル名が `_a` / `_b` .. のアルファベット連番でアップロードされた
 * メディア群を「A / B / C ...」の連番バッジ付きで縦に並べて表示し、
 * 選択 + コメント送信でコミュニケーションできるようにする
 * （Cursor Agent のチャット風 UI）。
 *
 * オーディオ候補はレターバッジがプレイヤーの再生ボタンを兼ねる（オプションモード）:
 * クリックで再生が始まり、同時にそのオプションが選択される。
 */

// ===== Styled Components =====

const SlideContainer = styled.div<{ $maxWidth?: string }>`
  width: 100%;
  ${p => (p.$maxWidth ? `max-width: ${p.$maxWidth};` : '')}
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 8px 0;
`;

/**
 * 候補グループ全体を包む外枠カード（TodoCardWrapper と同デザイン）。
 * 複数の候補（EntryCard）が1つのグループであることを視覚的に示す。
 */
const GroupCard = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  padding: 16px 12px 16px;
  border-radius: 6px;
  border: 1px solid var(--soremo-border);
  background-color: #fff;
  box-shadow: 2px 4px 8px 0px rgba(0, 0, 0, 0.05);
`;

/** 候補カード（GroupCard の内側）: 選択中は青枠 + 青シャドウで強調 */
const EntryCard = styled.div<{ $selected: boolean }>`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  padding: 16px 12px 16px;
  border-radius: 6px;
  border: 1px solid ${p => (p.$selected ? 'var(--soremo-blue)' : 'var(--soremo-border)')};
  background-color: #fff;
  box-shadow: ${p =>
    p.$selected
      ? '2px 4px 8px 0px rgba(0, 0, 0, 0.05), 0 0 0 3px rgba(0, 154, 206, 0.15)'
      : '2px 4px 8px 0px rgba(0, 0, 0, 0.05)'};
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
`;

/**
 * A / B / C の連番バッジ。
 * ホーム画面やることカードの連番（Counter type="todo"）と同デザインの
 * 32px 黒丸。アクティブ（選択中 or クライアント最終選択）は青。
 */
const EntryLetterBadge = styled.button.attrs({ className: 'bodytext' })<{ $active: boolean }>`
  line-height: 100%;
  letter-spacing: 0;
  user-select: none;

  width: 32px;
  height: 32px;
  flex-shrink: 0;

  border-radius: 50%;
  border: 1px solid var(--soremo-border);

  display: flex;
  align-items: center;
  justify-content: center;

  background: ${p => (p.$active ? 'var(--soremo-blue)' : 'var(--soremo-gray)')};
  color: #fff;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.15s ease;

  &:hover {
    transform: scale(1.08);
  }
`;

const EntryMedia = styled.div`
  width: 100%;
  position: relative;
`;

// ===== Props =====

export interface ComparisonEntryView {
  /** サフィックス文字を大文字化したラベル（A / B / C ...） */
  label: string;
  /** 表示するメディア（グループの最新テイク） */
  item: MediaItem;
}

export interface ComparisonSlideProps {
  baseName: string;
  entries: ComparisonEntryView[];
  /** 選択中メディアの ID（未選択は null） */
  selectedId: string | null;
  /** クライアント側が最後に選択・送信したメディアの ID（青キープ表示用） */
  keptId?: string | null;
  /** エントリクリック時（トグル処理は呼び出し側） */
  onSelect: (mediaItemId: string) => void;
  /** MediaPreview の ref 登録（再生制御用） */
  registerMediaRef: (mediaItemId: string, el: MediaPreviewHandle | null) => void;
  onPlayStateChange: (mediaItemId: string, playing: boolean) => void;
  /** MediaPreview へ渡す共通 props（MEDIA_PREVIEW_FULL_PROPS） */
  mediaPreviewProps: Record<string, unknown>;
  sizeMode: SizeMode;
  /** スライド全体の最大幅（デスクトップは 50vw 等） */
  maxWidth?: string;
  // ---- タグ ----
  editableTagSide: MediaTagSide | null;
  onTagToggle: (item: MediaItem, side: MediaTagSide, tag: MediaItemProducerTag | MediaItemClientTag) => void;
  // ---- コメント送信 ----
  onSend: (messageText: string) => void;
  isSending: boolean;
  // ---- ピン ----
  onPinClick?: () => string | void;
  isPinActive?: boolean;
}

// ===== Component =====

const ComparisonSlide: React.FC<ComparisonSlideProps> = ({
  baseName,
  entries,
  selectedId,
  keptId = null,
  onSelect,
  registerMediaRef,
  onPlayStateChange,
  mediaPreviewProps,
  sizeMode,
  maxWidth,
  editableTagSide,
  onTagToggle,
  onSend,
  isSending,
  onPinClick,
  isPinActive = false,
}) => {
  const selectedEntry = entries.find(e => e.item.id === selectedId);
  // ローカル選択がなければ、クライアント最終選択を青キープ表示
  const highlightId = selectedId ?? keptId;

  return (
    <SlideContainer $maxWidth={maxWidth}>
      <GroupCard>
        {entries.map(({ label, item }) => {
          const isHighlighted = item.id === highlightId;
          // オーディオはプレイヤーの再生ボタンがレターバッジ（A/B/C）を兼ねる
          // （再生開始 = 選択）。単独のバッジは重複するため出さない。
          const isAudio = toMediaType(item.file_type) === 'audio';
          return (
            <EntryCard key={item.id} $selected={isHighlighted}>
              {/* プロデューサータグ: カード右上に TakeLabel 風タブで表示 */}
              <ProducerTagOverlay
                item={item}
                editable={editableTagSide === 'producer'}
                onToggle={(i, tag) => onTagToggle(i, 'producer', tag)}
              />
              {!isAudio && (
                <EntryLetterBadge
                  type="button"
                  $active={isHighlighted}
                  onClick={() => onSelect(item.id)}
                  aria-pressed={item.id === selectedId}
                >
                  {label}
                </EntryLetterBadge>
              )}
              <EntryMedia>
                <MediaPreview
                  ref={(el: MediaPreviewHandle | null) => registerMediaRef(item.id, el)}
                  src={getMediaUrl(item)}
                  downloadUrl={item.original_url}
                  thumbnailSrc={item.thumbnail}
                  mediaType={toMediaType(item.file_type)}
                  sizeMode={sizeMode}
                  {...mediaPreviewProps}
                  waveformUrl={item.has_waveform && item.waveform_url ? item.waveform_url : undefined}
                  optionLabel={isAudio ? label : undefined}
                  optionSelected={isAudio ? isHighlighted : undefined}
                  onOptionSelect={
                    isAudio
                      ? () => {
                          // onSelect はトグルのため、選択済みなら再選択しない
                          // （再生ボタン再クリックで選択が外れるのを防ぐ）
                          if (item.id !== selectedId) onSelect(item.id);
                        }
                      : undefined
                  }
                  fileName={item.file_name}
                  fileId={item.id}
                  acrScanStatus={item.acr_scan_status}
                  acrScanResult={item.acr_scan_result}
                  acrScannedAt={item.acr_scanned_at}
                  createdAt={item.updated_at}
                  onPlayStateChange={(playing: boolean) => onPlayStateChange(item.id, playing)}
                />
              </EntryMedia>
              <ClientTagChips
                item={item}
                editable={editableTagSide === 'client'}
                onToggle={(i, tag) => onTagToggle(i, 'client', tag)}
              />
            </EntryCard>
          );
        })}

        <div className="u-w100">
          <CommentField
            showBottomAppBar={false}
            showPinIcon={true}
            onPinClick={onPinClick}
            isPinActive={isPinActive}
            pinTooltip={
              selectedEntry
                ? `【${selectedEntry.label}】にピン`
                : `${baseName} にピン`
            }
            showAttachIcon={false}
            allowEmptySend={!!selectedEntry && !isSending}
            onSend={onSend}
            placeholder={
              selectedEntry
                ? `【${selectedEntry.label}】についてコメント（省略可）`
                : `${baseName} を比較してコメント / メディアを選択して送信`
            }
          />
        </div>
      </GroupCard>
    </SlideContainer>
  );
};

export default ComparisonSlide;
